import { lazy, Suspense, type ComponentType, type LazyExoticComponent } from 'react';
import { BrowserRouter, Navigate, Route, Routes, useLocation } from 'react-router-dom';
import './App.css';

import LoginRoute from './routes/LoginRoute';
import RoleRoute from './routes/RoleRoute';
import PageSkeleton from './components/PageSkeleton';

const Login = lazy(() => import('./features/auth/login/Login'));
const ResetPassword = lazy(() => import('./features/auth/resetPassword/ResetPassword'));
const ForgotPassword = lazy(() => import('./features/auth/forgotPassword/ForgotPassword'));
const UserIndex = lazy(() => import('./features/dashboard/Accueil'));
const Index = lazy(() => import('./features/admin/users/Index'));
const DepartementsIndex = lazy(() => import('./features/departements/Index'));
const UserInfoPage = lazy(() => import('./features/admin/users/User.id'));
const PermissionsIndex = lazy(() => import('./features/rolesAndPermissions/pemissions/PermissionIndex'));
const SettingsPage = lazy(() => import('./features/settings/Settings'));
const RoleIndex = lazy(() => import('./features/rolesAndPermissions/roles/RoleIndex'));
const TimeSlotIndex = lazy(() => import('./features/settings/timeSlot/timeSlotIndex'));
const PointagesIndex = lazy(() => import('./features/pointages/PointageIndex'));
const LeaveIndex = lazy(() => import('./features/leaves/LeaveIndex'));
const TeamIndex = lazy(() => import('./features/teams/TeamIndex'));
const TeamInfoPage = lazy(() => import('./features/teams/Team.id'));
const ClientsIndex = lazy(() => import('./features/client/Index'));
const ProjectsIndex = lazy(() => import('./features/project'));
const ProjectDetails = lazy(() => import('./features/project/projet.id'));
const TasksIndex = lazy(() => import('./features/tasks'));
const Profile = lazy(() => import('./features/profile/Profile'));
const UserContractPage = lazy(() => import('./features/contract/contract.id'));
const AttendanceSettingsIndex = lazy(() => import('./features/settings/AttendanceSettings/AttendanceSettings'));
const TaskBilan = lazy(() => import('./features/tasks/bilan'));
const PresenceBilan = lazy(() => import('./features/pointages/bilan/bilan'));
const ProfessionalLeaveIndex = lazy(() => import('./features/professionnal_leave'));
const ContractsIndex = lazy(() => import('./features/contract/index'));
const ProfessionalLeaveDetail = lazy(() =>
  import('./features/professionnal_leave/professionnalLeave.id').then((m) => ({ default: m.ProfessionalLeaveDetail }))
);

function SuspenseRoute({ Component }: { Component: LazyExoticComponent<ComponentType<any>> }) {
  const location = useLocation();
  return (
    <Suspense key={location.pathname} fallback={<PageSkeleton />}>
      <Component />
    </Suspense>
  );
}

const withSkeleton = (Component: LazyExoticComponent<ComponentType<any>>) => (
  <SuspenseRoute Component={Component} />
);

function App() {
  return (
    <BrowserRouter>
      <Routes>
        <Route element={<LoginRoute />}>
          <Route
            path="/login"
            element={
              <Suspense fallback={null}>
                <Login />
              </Suspense>
            }
          />
          <Route path="/" element={<Navigate to="/login" replace />} />
        </Route>

        <Route
          path="/reset-password"
          element={
            <Suspense fallback={null}>
              <ResetPassword />
            </Suspense>
          }
        />
        <Route
          path="/forgot-password"
          element={
            <Suspense fallback={null}>
              <ForgotPassword />
            </Suspense>
          }
        />

        <Route element={<RoleRoute />}>
          <Route path="/dashboard/view" element={withSkeleton(UserIndex)} />
          <Route path="/dashboard/ma-fiche-employe" element={withSkeleton(UserInfoPage)} />
          <Route path="/dashboard/pointages" element={withSkeleton(PointagesIndex)} />
          <Route path="/dashboard/conges-et-absence" element={withSkeleton(LeaveIndex)} />
          <Route path="/dashboard/equipes" element={withSkeleton(TeamIndex)} />
          <Route path="/dashboard/equipes/:id" element={withSkeleton(TeamInfoPage)} />
          <Route path="/dashboard/clients" element={withSkeleton(ClientsIndex)} />
          <Route path="/dashboard/projets" element={withSkeleton(ProjectsIndex)} />
          <Route path="/dashboard/projets/:id" element={withSkeleton(ProjectDetails)} />
          <Route path="/dashboard/taches" element={withSkeleton(TasksIndex)} />
          <Route path="/dashboard/taches/:userId" element={withSkeleton(TasksIndex)} />
          <Route path="/profile" element={withSkeleton(Profile)} />
          <Route
            path="/dashboard/equipes/:teamId/utilisateur/:userId/taches"
            element={withSkeleton(TasksIndex)}
          />
          <Route path="/dashboard/bilan-des-taches" element={withSkeleton(TaskBilan)} />
          <Route path="/dashboard/bilan-des-taches/:userId" element={withSkeleton(TaskBilan)} />
          <Route path="/dashboard/bilan-de-presence" element={withSkeleton(PresenceBilan)} />
          <Route path="/dashboard/bilan-de-presence/:userId" element={withSkeleton(PresenceBilan)} />
          <Route path="/dashboard/sorties-professionnelles" element={withSkeleton(ProfessionalLeaveIndex)} />
          <Route path="/dashboard/mes-contrats" element={withSkeleton(UserContractPage)} />
          <Route
            path="/dashboard/sorties-professionnelles/:id"
            element={withSkeleton(ProfessionalLeaveDetail)}
          />
        </Route>

        <Route element={<RoleRoute requiredAuthorities={["ROLE_ADMIN", "ROLE_RH", "ROLE_DG"]} requireAll={false} />}>
          <Route path="/dashboard/users" element={withSkeleton(Index)} />
          <Route path="/dashboard/contrats" element={withSkeleton(ContractsIndex)} />
          <Route path="/dashboard/users/:id" element={withSkeleton(UserInfoPage)} />
          <Route path="/dashboard/user/:id/contrats" element={withSkeleton(UserContractPage)} />
        </Route>

        <Route path="/admin/dashboard">
          <Route element={<RoleRoute requiredAuthorities={["ROLE_ADMIN", "ROLE_RH"]} requireAll={false} />}>
            <Route path="users" element={withSkeleton(UserIndex)} />
            <Route path="settings/departements" element={withSkeleton(DepartementsIndex)} />
            <Route path="settings/plage-horaire" element={withSkeleton(TimeSlotIndex)} />
            <Route path="settings/roles" element={withSkeleton(RoleIndex)} />
            <Route path="settings" element={withSkeleton(SettingsPage)} />
            <Route path="pointages" element={withSkeleton(AttendanceSettingsIndex)} />
          </Route>

          <Route element={<RoleRoute requiredAuthorities={["ROLE_ADMIN", "SETTINGS_VIEW"]} requireAll={false} />}>
            <Route path="settings/permissions" element={withSkeleton(PermissionsIndex)} />
          </Route>
        </Route>

        <Route path="*" element={<Navigate to="/login" replace />} />
      </Routes>
    </BrowserRouter>
  );
}

export default App;